import { useLocale } from "@/contexts/IntlContext";
import { AnimatePresence, motion } from "framer-motion";
import { FormattedMessage } from "react-intl";

interface SegmentDataDisplayProps {
  titleKey: string;
  content: string | undefined;
  segmentNumber: number;
  totalSegments: number;
  showTotal?: boolean;
  isActive: boolean;
  hideContent?: boolean;
}

export default function SegmentDataDisplay({
  titleKey,
  content,
  segmentNumber,
  totalSegments,
  showTotal = false,
  isActive,
  hideContent = false,
}: SegmentDataDisplayProps) {
  const { locale } = useLocale();
  const isRTL = locale === "ar";

  return (
    <>
      <div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            transition: {
              duration: 0.6,
              ease: "easeOut",
            },
          }}
        >
          <div className="flex justify-center text-xs text-white/70">
            {isRTL ? (
              <>
                <span className={`rounded-[17.5px] flex items-center justify-center text-body text-white ${isActive ? "bg-primary" : "bg-gray"}  px-7`}>
                  {/* {segmentNumber} / {totalSegments} */}
                  <FormattedMessage id="common.step" />
                  {segmentNumber}
                  {showTotal && ` / ${totalSegments}`}
                </span>
              </>
            ) : (
              <>
                <span className={`rounded-[17.5px] flex items-center justify-center text-body text-white ${isActive ? "bg-primary" : "bg-gray"}  px-7`}>
                  <FormattedMessage id="common.step" />
                  {segmentNumber}
                  {showTotal && ` / ${totalSegments}`}
                </span>
              </>
            )}
          </div>
        </motion.div>
        <motion.div
          key={`title-${segmentNumber}`}
          className="mt-2.5 mb-7.5"
          initial={{ opacity: 0, y: 12 }}
          animate={{
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94],
            },
          }}
          exit={{
            opacity: 0,
            y: -8,
            transition: { duration: 0.2 },
          }}
        >
          <motion.h3
            className={`text-h3 font-medium transition-colors duration-500 text-center  ${isActive ? "text-primary" : "text-gray"
              }`}
            animate={{ scale: isActive ? 1.05 : 1 }}
            transition={{ duration: 0.3 }}
          >
            <FormattedMessage id={titleKey} />
          </motion.h3>
        </motion.div>
      </div>

      <AnimatePresence mode="wait">
        {!hideContent && (
        <motion.div
          key={`content-${segmentNumber}`}
          className="space-y-4"
          initial={{
            opacity: 0,
            y: 16,
          }}
          animate={{
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.5,
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.1,
            },
          }}
          exit={{
            opacity: 0,
            y: -12,
            transition: {
              duration: 0.3,
            },
          }}
        >
          <motion.p
            className={`text-sm leading-relaxed transition-colors duration-500 text-center ${isActive ? "text-black" : "text-black/50"
              }`}
            animate={{
              opacity: isActive ? 1 : 0.7,
            }}
            transition={{ duration: 0.3 }}
          >
            <FormattedMessage id={content} values={{ br: <br /> }} />
          </motion.p>
        </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
